import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { usePlausible } from 'next-plausible'
import { ReactElement } from 'react'

import Plus2 from '@core/shared/ui/icons/Plus2'

import {
  ChatOpenEventCreateInput,
  MessagePlatform
} from '../../../../__generated__/globalTypes'
import { useBlocks } from '../../../libs/block'
import { useJourney } from '../../../libs/JourneyProvider'
import { JourneyFields_chatButtons as ChatButton } from '../../../libs/JourneyProvider/__generated__/JourneyFields'
import {
  JourneyPlausibleEvents,
  keyify,
  templateKeyify
} from '../../../libs/plausibleHelpers'
import { getJourneyRTL } from '../../../libs/rtl'
import { MessageChatIcon } from '../../MessageChatIcon'

import {
  ChatButtonEventCreate,
  ChatButtonEventCreateVariables
} from './__generated__/ChatButtonEventCreate'

const AUTHORITY_LESS_SCHEME = /^(?:mailto|tel|sms):/i

export const CHAT_BUTTON_EVENT_CREATE = gql`
  mutation ChatButtonEventCreate($input: ChatOpenEventCreateInput!) {
    chatOpenEventCreate(input: $input) {
      id
    }
  }
`

export function ChatButtons(): ReactElement {
  const plausible = usePlausible<JourneyPlausibleEvents>()
  const { renderMode, journey } = useJourney()
  const { blockHistory } = useBlocks()
  const theme = useTheme()
  const { rtl } = getJourneyRTL(journey)

  const activeBlock = blockHistory[blockHistory.length - 1]
  const chatButtons = journey?.chatButtons
  const isWebsite = journey?.website === true
  const showDefault = renderMode === 'admin' && chatButtons?.length === 0

  const [chatButtonEventCreate] = useMutation<
    ChatButtonEventCreate,
    ChatButtonEventCreateVariables
  >(CHAT_BUTTON_EVENT_CREATE)

  const getColor = (
    primary: boolean,
    type: 'main' | 'background' | 'website' = 'main'
  ): string | undefined => {
    if (type === 'background') {
      return primary ? theme.palette.grey[100] : `${theme.palette.grey[200]}40`
    }
    if (type === 'main') {
      return primary ? theme.palette.grey[900] : theme.palette.grey[100]
    }
    if (type === 'website') {
      return primary ? theme.palette.grey[100] : `${theme.palette.grey[900]}88`
    }
  }

  const handleClick = (chatButton: ChatButton): void => {
    if (
      (renderMode === 'default' || renderMode === 'embed') &&
      chatButton.link != null
    ) {
      // mailto:/tel:/sms: are handed off by assigning location, not by opening a
      // popup: a popup to a non-HTTP scheme is blocked on iOS Safari and leaves a
      // stranded blank tab on Android Chrome. Matches handleAction's PhoneAction
      // and EmailAction cases.
      if (AUTHORITY_LESS_SCHEME.test(chatButton.link)) {
        window.location.href = chatButton.link
      } else {
        window.open(chatButton.link, '_blank')
      }
      const input: ChatOpenEventCreateInput = {
        id: chatButton?.id,
        blockId: activeBlock?.id,
        stepId: activeBlock?.id,
        value: chatButton?.platform
      }
      void chatButtonEventCreate({
        variables: {
          input: {
            blockId: activeBlock?.id,
            stepId: activeBlock?.id,
            value: chatButton?.platform
          }
        }
      })
      if (journey != null)
        plausible('footerChatButtonClick', {
          u: `${window.location.origin}/${journey.id}/${input.stepId}`,
          props: {
            ...input,
            key: keyify({
              stepId: input.stepId ?? '',
              event: 'footerChatButtonClick',
              blockId: input.blockId,
              target: `link:${chatButton.link}:${chatButton.platform}`,
              journeyId: journey?.id
            }),
            simpleKey: keyify({
              stepId: input.stepId ?? '',
              event: 'footerChatButtonClick',
              blockId: input.blockId,
              journeyId: journey?.id
            }),
            templateKey: templateKeyify({
              event: 'footerChatButtonClick',
              target: 'chat',
              journeyId: journey?.id
            })
          }
        })
    }
  }

  return (
    <Stack
      data-testid="StepFooterChatButtons"
      direction={rtl ? 'row' : 'row-reverse'}
      sx={{
        gap: 2
      }}
    >
      {chatButtons?.map((chatButton, index) => (
        <IconButton
          key={chatButton?.id}
          onClick={() => handleClick(chatButton)}
          sx={{
            height: 44,
            width: 44,
            backgroundColor: getColor(
              index === 0,
              isWebsite ? 'website' : 'background'
            ),
            '&:hover': {
              backgroundColor: getColor(
                index === 0,
                isWebsite ? 'website' : 'background'
              )
            },
            backdropFilter: 'blur(5px)',
            boxShadow: (theme) =>
              isWebsite ? `0px 6px 6px ${theme.palette.grey[900]}60` : undefined
          }}
        >
          <MessageChatIcon
            platform={chatButton.platform ?? MessagePlatform.custom}
            sx={{ color: getColor(index === 0, 'main') }}
          />
        </IconButton>
      ))}
      {showDefault && (
        <IconButton
          key="default"
          disabled
          sx={{
            height: 44,
            width: 44,
            outline: 'none',
            border: '3px dashed ',
            opacity: 0.5,
            borderColor: (theme) => theme.palette.grey[700]
          }}
        >
          <Plus2 sx={{ color: (theme) => theme.palette.grey[700] }} />
        </IconButton>
      )}
    </Stack>
  )
}
